import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {CommonService} from './common.service';
import {catchError, map} from 'rxjs/operators';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {InjectorInstant} from 'src/app/app.component';

const routes = {
  declinedSignDocument: () => `/anonymous/declined-document`,
  getDetail: () => `/anonymous/detail`,
  verifyToken: (hashKey, tenantId) => `/anonymous/verify-token/${hashKey}/${tenantId}`,
  getFileDetail: () => `/anonymous/file-detail`,
  getListCertSimCa: (hashKey, phoneNumber) => `/anonymous/get-certificate-simca/${hashKey}/${phoneNumber}`,
  getCertsCloudCa: () => `/anonymous/certs-cloud-ca`,
  getCertsVNeID: () => `/anonymous/certs-vneid`,
  getFileDetailByHashKey: () => `/anonymous/file-detail-no-filepath`,
  downloadMainContract: () => `/anonymous/download-file-detail`,
  downloadMainContractFiles: () => `/anonymous/download-files-detail`,
  signSimCANoImage: () => `/anonymous/sign-simca-no-image-multi-pos`,
  signSimCAImage: () => `/anonymous/sign-simca-image-multi-pos`,
  signCloudCaImage: () => `/anonymous/sign-cloud-ca-image`,
  signCloudCaNoImage: () => `/anonymous/sign-cloud-ca-no-image`,
  createHashWithImage: () => `/anonymous/generate-hash-image`,
  createHashNoImage: () => `/anonymous/generate-hash-no-image`,
  signUsb: () => `/anonymous/sign-usb-token`,
  getSignPluginName: (hashKey) => `/anonymous/download-plugins/${hashKey}`,
  getListEnterpriseSign: (hashKey: string, tenantId: string) => `/anonymous/list-enteprise/${hashKey}/${tenantId}`,
  viewContractPdf: (hashKey: string, tenantId: string) => `/anonymous/view-pdf-file/${hashKey}/${tenantId}`,
  checkStatusContract: (hashKey: string, tenantId: string) => `/anonymous/check-status/${tenantId}/${hashKey}`,
  downloadDocumentFile: (id: string, hashKey: string, tenantId: string) => `/anonymous/download-file-upload/${id}/${tenantId}/${hashKey}`,
  signWithImageNoCertificate: () => `/anonymous/sign/image-without-certificate-multi-pos`,
  generateTransaction: (hashKey: string, numberPhone: string, tenantId) => `/anonymous/otp/generate-transaction/${hashKey}/${numberPhone}/${tenantId}`,
  signOtpWithImage: () => `/anonymous/sign/otp-image-multi-pos`,
  SignOtpNoImage: () => '/anonymous/sign/otp-no-image-multi-pos',
  signDone: () => '/anonymous/sign/done',
  signCancel: () => '/anonymous/sign/cancel',
  getQuantityEkyc: (contractId: string, hashKey: string, tenantId: string) => `/anonymous/get-quantity-by-contractId/${hashKey}/${contractId}/${tenantId}`,
  valiadateEkycOtpSupport: (hashKey: string, tenantId: string) => `/anonymous/ekyc/validate-ekyc-otp-support/${hashKey}/${tenantId}`,
  createTimestampMoneyAnonymous: '/anonymous/sign-transaction/create',
  getStatusSign: () => `/anonymous/sign/get-sign-transaction-result`,
  checkExpireTime: (hashKey, tenant, hashLink) => `/anonymous/check-expire-time/${hashKey}/${tenant}/?hashLink=${hashLink}`,
  userRefuseApproveContract:'/anonymous/user-refuse-approve-contract',
  userApproveContract:'/anonymous/user-approve-contract',
  sendRequest:'/anonymous/verify-two-factor-external',
  signVNeID: () => `/anonymous/sign-vneid`,
};

@Injectable({
  providedIn: 'root'
})
export class AnonymousSignService {
  private documentInfoSource = new BehaviorSubject(null);
  public isShowReferencePdf = new BehaviorSubject(null);
  public documentInfo = this.documentInfoSource.asObservable();

  private listEnterpriseSource = new BehaviorSubject([]);
  public listEnterprise = this.listEnterpriseSource.asObservable();

  private enterpriseSignIdInFlowSource = new BehaviorSubject('');
  public enterpriseSignIdInFlow = this.enterpriseSignIdInFlowSource.asObservable();

  public userSignSource = new BehaviorSubject([]);
  public userSign = this.userSignSource.asObservable();
  public timeWaitSign: BehaviorSubject<any>;
  public selectedCert: BehaviorSubject<any>;
  constructor(private apiService: ApiService, private commonService: CommonService) {
    this.timeWaitSign = new BehaviorSubject<any>('');
    this.selectedCert = new BehaviorSubject<any>('');
  }

  public anonymousGetDetail(hashKey: string, tenantId: string, hashLink?: string) {
    const data = {hashKey, tenantId}
    if(hashLink) {
      data['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).post(routes.getDetail(), data)
      .pipe(map(res => {
        return res;
      }));
  }

  public verifyToken(hashKey: string, tenantId: string) {
    return InjectorInstant.get(ApiService).get(routes.verifyToken(hashKey, tenantId))
      .pipe(map(res => {
        return res;
      }));
  }

  public anonymousGetContractFile(hashKey: string, tenantId: string, pdfFilePath: string) {
    return InjectorInstant.get(ApiService).getFilePostMethod(routes.getFileDetail(), {hashKey, tenantId, pdfFilePath})
      .pipe(map(res => {
        return res;
      }));
  }

  public downloadMainContract(hashKey: string, tenantId: string) {
    return InjectorInstant.get(ApiService).getFilePostMethod(routes.downloadMainContract(), {hashKey, tenantId})
      .pipe(map(res => {
        return res;
      }));
  }

  public downloadMainContractFiles(hashKey: string, tenantId: string, hashLink?: string) {
    return InjectorInstant.get(ApiService).getFilePostMethod(routes.downloadMainContractFiles(), {hashKey, tenantId, hashLink})
      .pipe(map(res => {
        return res;
      }));
  }

  public anonymousGetContractFileByHashKey(hashKey: string, tenantId: string,pdfFilePath: string, errCallback?: any) {
    const body = {
      hashKey,
      tenantId,
      path: pdfFilePath
    };
    return InjectorInstant.get(ApiService).getFilePostMethod(routes.getFileDetailByHashKey(), body)
      .pipe(map((res) => {
        if (errCallback && !res.success) {
          errCallback(res);
        }
        return res;
      }, catchError((err, caught) => {
        if (errCallback) {
          errCallback(err);
        }
        return err;
      })));
  }

  public anonymousGetlistCertSimCa(hashKey: string, phoneNumber: string,tenant?: string, hashLink?: string) {
    const formdata = new FormData();
    if(tenant && hashLink) {
      formdata.append('tenant', tenant);
      formdata.append('hashLink', hashLink);
      return InjectorInstant.get(ApiService).checkType(routes.getListCertSimCa(hashKey, phoneNumber), formdata)
      .pipe(map(res => {
        return res;
      }));

    } else {
      return InjectorInstant.get(ApiService).post(routes.getListCertSimCa(hashKey, phoneNumber))
        .pipe(map(res => {
          return res;
        }, catchError((err, caught) => {
          return throwError(err);
        })));
    }

  }

  public getCertsCloudCa(hashKey: string, userId: string, providerName: string, hashLink?: string,tenantId?: string) {
    const data = {
      hashKey: hashKey,
      cloudCaUserId: userId,
      providerName: providerName
    };
    if(hashLink) {
      data['hashLink'] = hashLink;
    }
    if(tenantId) {
      data['tenantId'] = tenantId;
    }
    return InjectorInstant.get(ApiService).post(routes.getCertsCloudCa(), data)
      .pipe(map(res => {
        return res;
      }, catchError((err, caught) => {
        return throwError(err);
      })));
  }

  public getCertsVNeID(hashKey: string, userId: string, hashLink?: string,tenantId?: string) {
    const data = {
      hashKey: hashKey,
      citizenPid: userId,
    };
    if(hashLink) {
      data['hashLink'] = hashLink;
    }
    if(tenantId) {
      data['tenantId'] = tenantId;
    }
    return InjectorInstant.get(ApiService).post(routes.getCertsVNeID(), data)
      .pipe(map(res => {
        return res;
      }, catchError((err, caught) => {
        return throwError(err);
      })));
  }

  anonymousSignSimCaNoImage(sign: any, hashLink?: string) {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).post(routes.signSimCANoImage(), sign)
      .pipe(map(res => {
        return res;
      }));
  }

  anonymousSignSimCaImage(sign: any, hashLink?: string) {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).postFormData(routes.signSimCAImage(), sign)
      .pipe(map(res => {
        return res;
      }));
  }

  signCloudCaImage(sign: any, hashLink?: string) {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).postFormData(routes.signCloudCaImage(), sign)
      .pipe(map(res => {
        return res;
      }));
  }

  signCloudCaNoImage(sign: any, hashLink?: string) {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).post(routes.signCloudCaNoImage(), sign)
      .pipe(map(res => {
        return res;
      }));
  }

  createHashWithImage(hash: any, hashLink?: string) {
    if(hashLink) {
      hash['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).postFormData(routes.createHashWithImage(), hash)
      .pipe(map(res => {
        return res;
      }));
  }

  createHashNoImage(hash: any, hashLink?: string) {
    if(hashLink) {
      hash['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).post(routes.createHashNoImage(), hash)
      .pipe(map(res => {
        return res;
      }));
  }

  signUsb(certificate: any, hashLink?: string) {
    if(hashLink) {
      certificate['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).post(routes.signUsb(), certificate)
      .pipe(map(res => {
        return res;
      }));
  }

  getSignPluginName(hashKey: string) {
    return InjectorInstant.get(ApiService).get(routes.getSignPluginName(hashKey))
      .pipe(map(_ => {
        let osName = null;
    if (navigator.appVersion.indexOf("Win")!=-1) osName="WINDOWS";
    if (navigator.appVersion.indexOf("Mac")!=-1) osName="MACOS";
    if (navigator.appVersion.indexOf("X11")!=-1) osName="UNIX";
    if (navigator.appVersion.indexOf("Linux")!=-1) osName="LINUX";



    if(osName === "WINDOWS"){
      return "Hop dong dien tu.exe";
    }
    else if (osName === "LINUX" ) {
      return "Hop dong dien tu.deb"
    }
    else if (osName === "MACOS" ) {
      return "Hop dong dien tu.pkg"
    }
      }));
  }

  declineSignDocument(data: any) {
    return InjectorInstant.get(ApiService).post(routes.declinedSignDocument(), data)
      .pipe(map(res => {
        return res;
      }));
  }

  setDocumentInfo(document: any) {
    this.documentInfoSource.next(document);
  }

  setIsShowReferencePdf(pdf: any) {
    this.isShowReferencePdf.next(pdf);
  }

  setListEnterprise(lstEnterprise: any) {
    this.listEnterpriseSource.next(lstEnterprise);
  }

  setUserSign(userSign: any) {
    this.userSignSource.next(userSign);
  }

  setEnterpriseSignIdInFlow(enterpriseSignIdInFlow: string) {
    this.enterpriseSignIdInFlowSource.next(enterpriseSignIdInFlow);
  }

  getListEnterprise(hashKey: string, tenantId: string) {
    return InjectorInstant.get(ApiService).get(routes.getListEnterpriseSign(hashKey, tenantId))
      .pipe(map(res => {
        return res;
      }));
  }

  downloadPdf(hashKey: string, tenantId: string) {
    return InjectorInstant.get(ApiService).getFile(routes.viewContractPdf(hashKey, tenantId))
      .pipe(map(res => {
        return res;
      }));
  }

  checkStatusContract(hashKey: string, tenantId: string) {
    return InjectorInstant.get(ApiService).get(routes.checkStatusContract(hashKey, tenantId))
      .pipe(map(res => {
        return res;
      }));
  }

  downloadDocumentFile(id: string, hashKey: string, tenantId: string) {
    return InjectorInstant.get(ApiService).getFile(routes.downloadDocumentFile(id, hashKey, tenantId))
      .pipe(map(res => {
        return res;
      }));
  }

  signWithImageNoCertificate(data) {
    return InjectorInstant.get(ApiService).postFormData(routes.signWithImageNoCertificate(), data)
      .pipe(map(res => {
        return res;
      }));
  }

  generateTransaction(hashKey: string, numberPhone: string, tenantId: string, body: any, hashLink?: string) {
    if(hashLink) {
      body['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).post(routes.generateTransaction(hashKey, numberPhone, tenantId), body);
  }

  signOtpWithImage(sign, hashLink?: string) {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).postFormData(routes.signOtpWithImage(), sign)
      .pipe(map(res => {
        return res;
      }));
  }

  signOtpNoImage(sign, hashLink?: string) {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).post(routes.SignOtpNoImage(), sign)
      .pipe(map(res => {
        return res;
      }));
  }

  getHashKey(enterpriseIdEncode, tenantIdEncode, anonymousLinkLogEncode?: any) {
    const body = {enterpriseIdEncode: enterpriseIdEncode, tenantIdEncode: tenantIdEncode}
    if(anonymousLinkLogEncode) {
      body['anonymousLinkLogEncode'] = anonymousLinkLogEncode;
    }
    return InjectorInstant.get(ApiService).post('/anonymous/hash-key-sign-anonymous', body).pipe(map(res => {
      return res;
    }));
  }

  getViewPdfsForDocument(hashKey: string, tenantId: string) {
    return InjectorInstant.get(ApiService).post(`/anonymous/files-detail-no-filepath`, {hashKey, tenantId})
      .pipe(map(res => {
        return res;
      }));
  }

  signDone(sign) {
    return InjectorInstant.get(ApiService).post(routes.signDone(), sign)
      .pipe(map(res => {
        return res;
      }));
  }

  signCancel(sign, hashLink?: string) {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).post(routes.signCancel(), sign)
      .pipe(map(res => {
        return res;
      }));
  }

  getQuantityEkyc(contractId: string, hashKey: string, tenantId: string) {
    if (!contractId) return;
    return InjectorInstant.get(ApiService).get(routes.getQuantityEkyc(contractId, hashKey, tenantId)).pipe(map(res => { return res }));;
  }

  // validate-ekyc
  valiadateEkycOtpSupport(body: any, hashKey: string, tenantId: string): Observable<any> {
    return InjectorInstant.get(ApiService).post(routes.valiadateEkycOtpSupport(hashKey, tenantId), body);
  }

  createTimestampMoney(body) {
    return InjectorInstant.get(ApiService).post(routes.createTimestampMoneyAnonymous, body)
      .pipe(map(res => {
        return res;
      }));
  }
  // sendRequest(body:any) {
  //   return InjectorInstant.get(ApiService).post(routes.sendRequest, body)
  //     .pipe(map(res => {
  //       return res;
  //     }));
  // }
  sendRequest(body:any) {
    return this.apiService.postWithToken(routes.sendRequest, body)
          .pipe(map(res => {
            return res;
    }));
  }

  getStatusSign(signTransactionCode) {
    return InjectorInstant.get(ApiService).post(routes.getStatusSign(), signTransactionCode)
      .pipe(map(res => {
        return res;
      }));
  }

  public checkExpireTime(hashKey: string, tenant: string,hashLink: string) {
    return InjectorInstant.get(ApiService).post(routes.checkExpireTime(hashKey, tenant, hashLink))
      .pipe(map(res => {
        return res;
      }));
  }

  // Phe duyet hop dong
  userApproveContract(data) {
    return InjectorInstant.get(ApiService).post(routes.userApproveContract, data)
      .pipe(map(res => {
        return res;
      }));
    }
    // Tu choi phe duyet hop dong
  userRefuseApproveContract(data) {
    return InjectorInstant.get(ApiService).post(routes.userRefuseApproveContract, data)
      .pipe(map(res => {
        return res;
      }));
  }

  signVNeID(sign: any, hashLink?: string) {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    return InjectorInstant.get(ApiService).postFormData(routes.signVNeID(), sign)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * Chuyen field image (File/Blob) trong body sang chuoi base64 de gui JSON thay multipart.
   */
  private async toSignBodyBase64(sign: any) {
    const body = {...sign};
    if (body.image instanceof Blob) {
      body.image = await this.commonService.getFileBase64(body.image as File);
    }
    return body;
  }

  /**
   * Version Base64 cua anonymousSignSimCaImage - anh chu ky gui JSON thay multipart binary.
   */
  anonymousSignSimCaImageBase64 = async (sign: any, hashLink?: string) => {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    const body = await this.toSignBodyBase64(sign);
    return InjectorInstant.get(ApiService).postFileBase64(routes.signSimCAImage(), body)
      .pipe(map(res => {
        return res;
      })).toPromise();
  }

  /**
   * Version Base64 cua signCloudCaImage - anh chu ky gui JSON base64.
   */
  signCloudCaImageBase64 = async (sign: any, hashLink?: string) => {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    const body = await this.toSignBodyBase64(sign);
    return InjectorInstant.get(ApiService).postFileBase64(routes.signCloudCaImage(), body)
      .pipe(map(res => {
        return res;
      })).toPromise();
  }

  /**
   * Version Base64 cua createHashWithImage - anh chu ky gui JSON base64.
   */
  createHashWithImageBase64 = async (hash: any, hashLink?: string) => {
    if(hashLink) {
      hash['hashLink'] = hashLink;
    }
    const body = await this.toSignBodyBase64(hash);
    // multipart cũ tự stringify array (signPositions/position) - JSON phai convert tay de BE nhan String
    if (body.signPositions && typeof body.signPositions !== 'string') {
      body.signPositions = JSON.stringify(body.signPositions);
    }
    if (body.position && typeof body.position !== 'string') {
      body.position = JSON.stringify(body.position);
    }
    return InjectorInstant.get(ApiService).postFileBase64(routes.createHashWithImage(), body)
      .pipe(map(res => {
        return res;
      })).toPromise();
  }

  /**
   * Version Base64 cua signWithImageNoCertificate - anh chu ky gui JSON base64.
   */
  signWithImageNoCertificateBase64 = async (data) => {
    const body = await this.toSignBodyBase64(data);
    return InjectorInstant.get(ApiService).postFileBase64(routes.signWithImageNoCertificate(), body)
      .pipe(map(res => {
        return res;
      })).toPromise();
  }

  /**
   * Version Base64 cua signOtpWithImage - anh chu ky gui JSON base64.
   */
  signOtpWithImageBase64 = async (sign, hashLink?: string) => {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    const body = await this.toSignBodyBase64(sign);
    return InjectorInstant.get(ApiService).postFileBase64(routes.signOtpWithImage(), body)
      .pipe(map(res => {
        return res;
      })).toPromise();
  }

  /**
   * Version Base64 cua signVNeID - anh chu ky gui JSON base64.
   */
  signVNeIDBase64 = async (sign: any, hashLink?: string) => {
    if(hashLink) {
      sign['hashLink'] = hashLink;
    }
    const body = await this.toSignBodyBase64(sign);
    return InjectorInstant.get(ApiService).postFileBase64(routes.signVNeID(), body)
      .pipe(map(res => {
        return res;
      })).toPromise();
  }

}
